/**
 * API functions and types for interacting with Journey backend endpoints.
 * Provides methods to fetch, review, approve, and create journeys.
 */

import { authApi } from '../auth/api';
import { JourneyType } from '../../types/journey';
import { OmioRoute, resolveOmioRoute, omioPositionParams } from './omioPositions';

export type { OmioRoute } from './omioPositions';

/**
 * Represents a change operation that can be performed while editing a journey.
 */
type BaseJourneyChange = { sequence: number };

export type JourneyChange =
  | (BaseJourneyChange & { type: 'add_stop'; position: number; stopId: string; stop: { id?: string; city?: string; country?: string }; method?: 'manual' | 'ai_assisted'; instructions?: string })
  | (BaseJourneyChange & { type: 'remove_stop'; stopId: string })
  | (BaseJourneyChange & { type: 'replace_stop'; stopId: string; newStop: { city?: string; country?: string }; method?: 'manual' | 'ai_assisted'; instructions?: string })
  | (BaseJourneyChange & { type: 'update_leg_transport'; legId: string; transportMode: string });

/**
 * Standard response format for journey editing API calls.
 * @template T - The type of journey object returned (if successful)
 */
export interface EditResponse<T = any> {
  success: boolean;
  journey?: T;
  notes?: string | null; // Notes from LLM response
  error?: { code: string; message: string; retryable?: boolean };
}

/**
 * Fetches detailed information of a journey by its ID.
 *
 * @template T - The return data type (defaults to any)
 * @param journeyId - Unique identifier for the journey
 * @param opts.initialView - Pass true ONLY on the page-mount fetch: the backend
 *   fires the server-side `journey_detail_viewed` activation event when it sees
 *   `?view=1`, so background refetches must leave it off.
 * @returns Promise resolving to journey detail data
 */
export async function fetchJourneyDetail<T = any>(journeyId: string, opts?: { initialView?: boolean }): Promise<T> {
  const res = await authApi.get(`/journeys/${journeyId}`, { params: opts?.initialView ? { view: '1' } : undefined });
  return res.data as T;
}

/**
 * Submits a list of proposed journey changes for review/AI processing.
 * Intended for the refinement/review step before final approval.
 *
 * @template T - The return data type (defaults to any)
 * @param journeyId - ID of the journey to be reviewed
 * @param changes - Array of change operations (add, remove, update, etc.)
 * @returns Promise resolving to an EditResponse object with review results or error
 */
export async function submitJourneyReview<T = any>(journeyId: string, changes: JourneyChange[], signal?: AbortSignal): Promise<EditResponse<T>> {
  try {
    const res = await authApi.post(`/journeys/${journeyId}/review`, { changes }, { signal });
    return res.data as EditResponse<T>;
  } catch (error: any) {
    // Check if this is an abort error (user cancelled the request)
    const isAbortError = error?.name === 'AbortError' ||
                        error?.message?.includes('aborted') ||
                        error?.message?.includes('cancelled') ||
                        error?.code === 'ERR_CANCELED';

    if (isAbortError) {
      return { success: false, error: { code: 'ABORTED', message: 'Request was cancelled', retryable: false } };
    }

    const code = error?.response?.data?.error?.code || 'REVIEW_ERROR';
    const message = error?.response?.data?.error?.message || 'Unable to process changes. Please try again.';
    const retryable = Boolean(error?.response?.data?.error?.retryable);
    return { success: false, error: { code, message, retryable } };
  }
}

/**
 * Approves and saves a journey update (PUT).
 * Used after review/sample-refinement and upon user confirmation.
 *
 * @template T - The return data type (defaults to any)
 * @param journeyId - ID of the journey to update
 * @param journey - The fully processed journey object (post-edits)
 * @returns Promise resolving to EditResponse indicating update results or error
 */
export async function approveUpdateJourney<T = any>(journeyId: string, journey: JourneyType): Promise<EditResponse<T>> {
  try {
    const res = await authApi.put(`/journeys/${journeyId}`, { journey });
    return res.data as EditResponse<T>;
  } catch (error: any) {
    const code = error?.response?.data?.error?.code || 'UPDATE_ERROR';
    const message = error?.response?.data?.error?.message || 'Unable to update journey. Please try again.';
    return { success: false, error: { code, message } };
  }
}

/**
 * Renames a journey (metadata-only update). Hits the same PUT endpoint as the
 * approval flow, but without a `journey` body it takes the simple
 * title/comments branch and leaves stops/legs untouched.
 *
 * @param journeyId - ID of the journey to rename
 * @param title - The new title
 */
export async function renameJourney<T = any>(journeyId: string, title: string): Promise<EditResponse<T>> {
  try {
    const res = await authApi.put(`/journeys/${journeyId}`, { title });
    return res.data as EditResponse<T>;
  } catch (error: any) {
    const code = error?.response?.data?.error?.code || 'UPDATE_ERROR';
    const message = error?.response?.data?.error?.message || 'Unable to rename journey. Please try again.';
    return { success: false, error: { code, message } };
  }
}

/**
 * Updates a journey's travel dates (metadata-only update). Hits the simple
 * title/comments/dates branch of the PUT endpoint, leaving stops/legs untouched.
 * Pass `null` to clear a date. Values are `yyyy-mm-dd` strings.
 */
export async function updateJourneyDates<T = any>(
  journeyId: string,
  departureDate: string | null,
  returnDate: string | null,
): Promise<EditResponse<T>> {
  try {
    const res = await authApi.put(`/journeys/${journeyId}`, { departureDate, returnDate });
    return res.data as EditResponse<T>;
  } catch (error: any) {
    const code = error?.response?.data?.error?.code || 'UPDATE_ERROR';
    const message = error?.response?.data?.error?.message || 'Unable to update the dates. Please try again.';
    return { success: false, error: { code, message } };
  }
}

/**
 * Deletes a journey (owner only). The backend also best-effort removes the
 * associated LLM conversation.
 *
 * @param journeyId - ID of the journey to delete
 */
export async function deleteJourney(journeyId: string): Promise<{ success: boolean; error?: { message: string } }> {
  try {
    await authApi.delete(`/journeys/${journeyId}`);
    return { success: true };
  } catch (error: any) {
    const message = error?.response?.data?.error?.message || 'Unable to delete journey. Please try again.';
    return { success: false, error: { message } };
  }
}

/**
 * Duplicate a journey (stops + legs) into the same trip, under a new name.
 * The copy never becomes the trip's main route, and it gets fresh affiliate
 * links + its own AI thread — see backend journeyEditService.copyJourney.
 */
export async function copyJourney(journeyId: string, title: string): Promise<{ id: string; title: string | null; tripId: string | null }> {
  const res = await authApi.post(`/journeys/${journeyId}/copy`, { title });
  return res.data;
}

/**
 * Native (deterministic) editor calls. Each mutates the saved journey and returns
 * the updated journey in v2 shape (the affected legs recompute server-side and
 * are re-priced asynchronously). They throw on error; callers catch + toast.
 */
export async function addJourneyStop(
  journeyId: string,
  body: { afterStopId: string | null; city: string; country?: string | null; latitude: number; longitude: number; suggestion?: StopSuggestionPick },
): Promise<any> {
  const res = await authApi.post(`/journeys/${journeyId}/stops`, body);
  return res.data;
}

/** Where a described place goes: after a stop (null = before the first) or instead of one. */
export type StopSuggestionTarget =
  | { action: 'add'; afterStopId: string | null }
  | { action: 'replace'; stopId: string };

/** What choosing a suggested place costs in travel (the hint on its card). */
export interface StopSuggestionTravel {
  /** 'detour': extra minutes over the path it changes (negative = less travel);
   *  'from' / 'to': the one new leg at either end of the route. */
  kind: 'detour' | 'from' | 'to';
  minutes: number | null;
  city?: string;
}

export interface StopSuggestion {
  city: string;
  country: string | null;
  region: string | null;
  latitude: number;
  longitude: number;
  reason: string;
  travel: StopSuggestionTravel;
}

/** Sent back with the add/replace call when the place came from a suggestion (analytics only). */
export interface StopSuggestionPick {
  requestId: string | null;
  /** 1-based position of the picked card. */
  rank: number;
  count: number;
}

/**
 * "Describe a place": 2–3 real places Jody suggests for an add or a replace,
 * geocoded server-side (backend services/stopSuggestionService.js). Read-only;
 * the pick goes through addJourneyStop / replaceJourneyStop like any city.
 */
export async function suggestJourneyStops(
  journeyId: string,
  target: StopSuggestionTarget,
  instructions: string,
): Promise<{ requestId: string | null; suggestions: StopSuggestion[] }> {
  const res = await authApi.post(`/journeys/${journeyId}/stops/suggest`, { ...target, instructions });
  return res.data;
}

export async function removeJourneyStop(journeyId: string, stopId: string): Promise<any> {
  const res = await authApi.delete(`/journeys/${journeyId}/stops/${stopId}`);
  return res.data;
}

export async function replaceJourneyStop(
  journeyId: string,
  stopId: string,
  body: { city: string; country?: string | null; latitude: number; longitude: number; suggestion?: StopSuggestionPick },
): Promise<any> {
  const res = await authApi.patch(`/journeys/${journeyId}/stops/${stopId}`, body);
  return res.data;
}

export async function changeLegMode(journeyId: string, legId: string, transportMode: string): Promise<any> {
  const res = await authApi.patch(`/journeys/${journeyId}/legs/${legId}`, { transportMode });
  return res.data;
}

/**
 * Planned/Unplanned status toggles (prototype §7, simplified). Persist whether
 * the stay at a stop / the ticket for a leg is sorted. Return the updated journey.
 */
export async function setStopPlanned(journeyId: string, stopId: string, planned: boolean): Promise<any> {
  const res = await authApi.patch(`/journeys/${journeyId}/stops/${stopId}`, { planned });
  return res.data;
}

/** `via` feeds the server-side `leg_planned_toggled` event: 'save_dialog' when
 *  the toggle came from the "Mark planned and save" dialog, default 'sheet'. */
export async function setLegPlanned(journeyId: string, legId: string, planned: boolean, via?: 'sheet' | 'save_dialog'): Promise<any> {
  const res = await authApi.patch(`/journeys/${journeyId}/legs/${legId}`, { planned }, { params: via ? { via } : undefined });
  return res.data;
}

/**
 * User-editable costs & times: per-mode values overriding the estimates on one
 * leg (schema doc: `userOverrides`). Sparse map, FULL-MAP REPLACE on save —
 * send the complete desired map ({} or null clears everything). Estimates are
 * untouched; the journey's totals recompute server-side (they prefer the
 * current mode's override). Returns the updated journey.
 */
export interface LegModeOverride {
  costCents?: number;
  durationMinutes?: number;
  /** Server-stamped; ignored on save. */
  editedAt?: string;
}
export type LegUserOverrides = Record<string, LegModeOverride>;

/** Per-mode LLM price cache on a leg (read-only on the FE; schema doc `priceEstimates`). */
export type LegPriceEstimates = Record<string, { lowCents: number; highCents: number; estimatedAt?: string }>;

/**
 * User-entered ticket details for a leg (leg sheet Tickets section; schema doc
 * `ticketDetails`). Full-object replace on save; null clears everything. The
 * date, when set, re-anchors the timeline's expected schedule.
 */
export interface LegTicketDetails {
  date?: string | null; // YYYY-MM-DD
  time?: string | null; // HH:mm
  extraInfos?: string | null;
}

export async function setLegTicketDetails(journeyId: string, legId: string, ticketDetails: LegTicketDetails | null): Promise<any> {
  const res = await authApi.patch(`/journeys/${journeyId}/legs/${legId}`, { ticketDetails });
  return res.data;
}

/**
 * User-entered accommodation details for one stop (stop sheet "Where you'll
 * stay" — ticketDetails' twin on the stop side, schema doc `stayDetails`).
 * Full-object replace on save; null clears everything.
 */
export interface StopStayDetails {
  placeName?: string | null;
  address?: string | null;
  bookedVia?: string | null;
  costCents?: number | null;
}

export async function setStopStayDetails(journeyId: string, stopId: string, stayDetails: StopStayDetails | null): Promise<any> {
  const res = await authApi.patch(`/journeys/${journeyId}/stops/${stopId}`, { stayDetails });
  return res.data;
}

/** Booking.com search link for a stop's stay (server-built so the CTA click
 *  is tracked server-side and the affiliate id, once registered, is one env
 *  var away — docs/integrations/booking.md). */
export async function fetchStopBookingLink(
  journeyId: string,
  stopId: string,
  context?: { checkin?: string | null; checkout?: string | null; source?: 'timeline' | 'stop_sheet' },
): Promise<{ url: string }> {
  const params: Record<string, string> = {};
  if (context?.checkin && /^\d{4}-\d{2}-\d{2}$/.test(context.checkin)) params.checkin = context.checkin;
  if (context?.checkout && /^\d{4}-\d{2}-\d{2}$/.test(context.checkout)) params.checkout = context.checkout;
  if (context?.source) params.source = context.source;
  const res = await authApi.get(`/journeys/${journeyId}/stops/${stopId}/affiliate/booking`, { params });
  return res.data;
}

export async function setLegOverrides(journeyId: string, legId: string, userOverrides: LegUserOverrides | null): Promise<any> {
  const res = await authApi.patch(`/journeys/${journeyId}/legs/${legId}`, { userOverrides });
  return res.data;
}

/**
 * The night train of a leg, as the backend describes it
 * (backend/services/nightTrains/legMode.js; spec:
 * docs/product/night-trains/spec/night-trains-spec.md §6.5). Times are local
 * station times. Everything comes from the timetable data: nothing here is
 * an estimate, and nothing may be added to it in the UI.
 */
export interface NightTrainStation {
  stationId: string;
  name: string;
  /** in_town: the stop itself; nearby: 15 to 80 km away; reach: with a connection. */
  tier: 'in_town' | 'nearby' | 'reach';
  /** From the leg's stop to the station: by road when `byRoad`, else in a straight line. */
  distanceKm: number;
  byRoad?: boolean;
  /** HH:MM */
  time: string;
  /** Arrival only: days after boarding. */
  dayOffset?: number;
}
export interface LegNightTrain {
  serviceId: string;
  lineKey: string;
  productName: string;
  /** Product name + train number, e.g. "Nightjet NJ 408". */
  displayName: string;
  operatorKey: string;
  operatorName: string | null;
  bookingUrl: string;
  preferredChannel: 'omio' | 'operator';
  boarding: NightTrainStation;
  arrival: NightTrainStation;
  durationMin: number;
  nightsOnBoard: number;
  hasSleeper: boolean;
  hasCouchette: boolean;
  hasSeats: boolean;
  bedsSource: string;
  beds: 'beds' | 'seats_only';
  weekdays: string[];
  /** "Nightly" or "Mon, Wed, Fri". */
  weekdayLabel: string;
  dateKnown: boolean;
  date: string | null;
  /** null when the date is unknown. */
  runsOnDate: boolean | null;
  nearestRuns: string[];
  /** The next three runs, from the leg's date when known, else from today. */
  nextRuns?: string[];
  /** Stations the train calls at, boarding and getting off included. */
  stations?: Array<{ name: string; arrival: string | null; departure: string | null }>;
  /** One-tap date shifts when the train does not run on the leg's date:
   *  leaving `deltaNights` later moves that many nights from the next stop
   *  to the previous one (earlier: the reverse). */
  shiftOptions?: Array<{ date: string; deltaNights: number }>;
  horizon: boolean;
  horizonLabel: string | null;
  expectedOnDate: boolean | null;
  shiftAvailable: boolean;
  /** Data date for the "Timetable checked" trust mark. */
  dataDate: string;
  /** The current data no longer has this train: stored values are shown. */
  timetableChanged: boolean;
  /** The stop whose hotel night moved on board, if any. */
  nightTakenFromStopId?: string | null;
}

/** Airport pair for a flight leg (backend: services/airports.airportInfo). */
export interface LegAirport {
  iata: string | null;
  name: string;
  fullName?: string | null;
  city?: string | null;
  /** Road distance from the city centre (straight line x 1.3), rounded. */
  distanceFromCityKm: number;
  /** Rough city-to-airport transfer time at urban speeds, minutes. */
  transferMinutes?: number | null;
}
/** Airports serving one endpoint city, nearest first. */
export interface LegAirportCity {
  city: string;
  airports: LegAirport[];
}
/** Both ends of a flight leg. A LIST per city: no dataset field separates a
 *  commercial hub from a private-aviation field, so we show them all. */
export type LegAirportPair = { from: LegAirportCity; to: LegAirportCity };

/**
 * Read-only previews for the leg sheet's live mode compare (nothing persists).
 * Facts are deterministic (backend engine); the price band is an LLM estimate.
 */
export async function previewLegMode(
  journeyId: string,
  legId: string,
  mode: string,
): Promise<{ transportMode: string; durationMinutes: number | null; distanceKm: number | null; co2eKg: number | null; airports?: LegAirportPair | null }> {
  const res = await authApi.get(`/journeys/${journeyId}/legs/${legId}/preview`, { params: { mode } });
  return res.data;
}

export async function previewLegPrice(
  journeyId: string,
  legId: string,
  mode: string,
): Promise<{ lowCents: number; highCents: number }> {
  const res = await authApi.get(`/journeys/${journeyId}/legs/${legId}/price-preview`, { params: { mode } });
  return res.data;
}

/**
 * Compare-transport-options dialog (read-only). `compareLegModes` returns the
 * deterministic facts table for every comparable mode instantly; the price
 * bands come from ONE batched LLM call via `compareLegPrices`.
 */
export interface CompareModeRow {
  mode: string;
  applicable: boolean;
  free: boolean;
  durationMinutes: number | null;
  distanceKm: number | null;
  co2eKg: number | null;
  /** Flight rows only: the airport pair behind the estimate. */
  airports?: LegAirportPair | null;
  /** Night-train row only: the train, when the timetable data has one. */
  nightTrain?: LegNightTrain | null;
  /** Night-train row only. 'unknown' = no timetable data for the area, which
   *  is not the same as 'none' (no direct night train). The journey's legs
   *  carry the same two fields. */
  nightTrainStatus?: 'available' | 'none' | 'too_short' | 'unknown';
  /** Night-train row only: the line shown for 'unknown'. */
  nightTrainNote?: string | null;
}

export async function compareLegModes(
  journeyId: string,
  legId: string,
): Promise<{ legId: string; from: string; to: string; distanceKm: number | null; currentMode: string; modes: CompareModeRow[] }> {
  const res = await authApi.get(`/journeys/${journeyId}/legs/${legId}/compare`);
  return res.data;
}

export async function compareLegPrices(
  journeyId: string,
  legId: string,
  modes: string[],
): Promise<{ prices: Array<{ mode: string; lowCents: number; highCents: number }> }> {
  const res = await authApi.get(`/journeys/${journeyId}/legs/${legId}/compare-prices`, { params: { modes: modes.join(',') } });
  return res.data;
}

/**
 * Where a leg's night train is booked: the Omio affiliate link when the
 * operator is sold there, else the operator's own site (with Jody's UTM
 * parameters, added by the backend). Called at the moment of the click.
 * `route` as in fetchLegOmioLink; the pages pass it only when the train is
 * sold on Omio, so no lookup runs for a click headed to the operator's site.
 */
export async function fetchNightTrainBookingLink(
  journeyId: string,
  legId: string,
  ctx?: { source?: 'timeline' | 'leg_sheet'; daysUntilDeparture?: number | null; date?: string | null; route?: OmioRoute | null },
): Promise<{ url: string; channel: 'omio' | 'operator'; operatorName: string | null }> {
  const params: Record<string, string | number> = {};
  if (ctx?.source) params.source = ctx.source;
  if (ctx?.daysUntilDeparture != null) params.daysUntilDeparture = ctx.daysUntilDeparture;
  if (ctx?.date) params.date = ctx.date;
  Object.assign(params, omioPositionParams(await resolveOmioRoute(ctx?.route)));
  const res = await authApi.get(`/journeys/${journeyId}/legs/${legId}/night-train/booking`, { params });
  return res.data;
}

/** One night train nearby (backend: journeyEditService.searchNightTrainsNearby). */
export interface NightTrainNearbyOption {
  key: string;
  /** The night train this part of the trip uses already. */
  current?: boolean;
  /** The stops that would be added, named as they will be in the trip (the
   *  town of the station); null where no stop is added. */
  boardingTown?: string | null;
  arrivalTown?: string | null;
  /** The train, as the leg sheet shows one. Its stations' `distanceKm` is from the leg's two stops. */
  train: LegNightTrain;
  /** From the leg's two places to the train's two stations: by road where
   *  the map provider has a distance (`fromByRoad`), else in a straight line.
   *  A station in town is not measured. */
  fromKm: number;
  toKm: number;
  fromByRoad?: boolean;
  toByRoad?: boolean;
  /** The station is farther than a nearby one: it becomes a change, a stop with 0 nights. */
  changeAtBoarding: boolean;
  changeAtArrival: boolean;
  /** Estimated minutes of the stretches that are not by night train (by train); null where there is no change. */
  toBoardingMinutes: number | null;
  fromArrivalMinutes: number | null;
}
export interface NightTrainsNearby {
  legId: string;
  from: string;
  to: string;
  status: 'ok' | 'too_short' | 'no_coords';
  /** Between the two places, in a straight line (what the minimum length is measured with). */
  legKm: number | null;
  /** The radius searched: the one asked for, else the first that finds a night train. */
  radiusKm: number;
  radiiKm: number[];
  /** Night trains found per radius: one that finds none is not offered. */
  counts?: Record<string, number>;
  /** The leg's date when known (ISO day). */
  date: string | null;
  /** "No timetable data for Spain yet", when an end is in a country without data. */
  note: string | null;
  /** What it takes for a night train to be listed (the backend's numbers). */
  rules: { minLegKm: number; boardFrom: string; boardTo: string; getOffEarliest: string; minOnboardHours: number; minCoveredShare: number; maxPathRatio: number };
  /** What adding a train takes the place of. */
  replaces: { legs: Array<{ from: string; to: string; transportMode: string | null }>; stops: string[]; hasUserData: boolean };
  options: NightTrainNearbyOption[];
}

/**
 * What "Search night trains" would find for a leg, worked out by the backend
 * when the journey is read (services/nightTrains/nearby.searchSummary): the
 * pages say how many night trains there are, and do not offer a search that
 * finds none. On every leg of the owner's journey as `nightTrainSearch`;
 * null where it was not worked out (the search stays offered).
 */
export interface NightTrainSearchSummary {
  /** 'unknown': none found and no timetable data for the countries of both
   *  places, which is never "no night trains". With data for one of the two
   *  the search has looked across the border, and 'none' is what it found.
   *  'none_on_dates': none on the leg's date or
   *  within three days of it, while some run on other dates. */
  status: 'found' | 'none' | 'none_on_dates' | 'unknown' | 'too_short';
  /** Night trains within the widest radius, other than the one in use; at most what the dialog lists. */
  count: number;
  /** There are that many or more ("5+"). */
  more: boolean;
  /** The line shown for 'unknown': "No timetable data for Spain yet". */
  note: string | null;
}

/**
 * Whether the trip form offers "Night train" next to "Train" (night trains
 * spec §4 and §9, `nightTrain.formOptionEnabled` in the backend's config;
 * `GET /api/public/night-trains/config`). False when the backend cannot say.
 */
export async function fetchNightTrainFormOption(): Promise<boolean> {
  try {
    const res = await authApi.get('/public/night-trains/config');
    return Boolean(res.data && res.data.formOptionEnabled);
  } catch {
    return false;
  }
}

/**
 * "Search night trains nearby" for a leg without a direct night train
 * (docs/product/night-trains/README.md). Guests may search.
 */
export async function searchNightTrainsNearby(journeyId: string, legId: string, radiusKm?: number): Promise<NightTrainsNearby> {
  const res = await authApi.get(`/journeys/${journeyId}/legs/${legId}/night-train/nearby`, { params: radiusKm ? { radius: radiusKm } : {} });
  return res.data;
}

/** Add the picked night train to the route: the change stop(s), the legs,
 *  the night on board. Returns the journey. Accounts only, like every edit. */
export async function addNightTrainNearby(journeyId: string, legId: string, train: LegNightTrain): Promise<any> {
  const res = await authApi.post(`/journeys/${journeyId}/legs/${legId}/night-train/nearby`, {
    serviceId: train.serviceId,
    boardingStationId: train.boarding.stationId,
    arrivalStationId: train.arrival.stationId,
  });
  return res.data;
}

/** Move a night between the previous and the next stop so the night train
 *  runs on the leg's date (one of the leg's nightTrain.shiftOptions). */
export async function shiftNightTrainDate(journeyId: string, legId: string, date: string): Promise<any> {
  const res = await authApi.post(`/journeys/${journeyId}/legs/${legId}/night-train/shift`, { date });
  return res.data;
}

/**
 * Omio affiliate tracking link for a leg's route (integration doc:
 * docs/integrations/omio-impact.md). The backend creates it via impact.com on
 * the first call and caches it on the leg, so repeat calls are instant.
 *
 * This call happens only at the moment of a click, so the backend treats it AS
 * the click and fires `affiliate_link_clicked` server-side (ad-blocker-proof);
 * `source` and `daysUntilDeparture` are event context. `date` (the travel day
 * the UI shows for the leg, ISO) goes INTO the deep link: together with the
 * trip's traveler count it pre-fills Omio's search form. `route` (the leg's
 * two stops) is looked up in Omio's suggester first (omioPositions.ts): the
 * position ids make the link Omio's pre-filled search, which works for every
 * place Omio knows; without them it is the route page, retired for most pairs.
 */
export async function fetchLegOmioLink(
  journeyId: string,
  legId: string,
  context?: { source?: 'timeline' | 'leg_sheet'; daysUntilDeparture?: number | null; date?: string | null; route?: OmioRoute | null },
): Promise<{ url: string; deepLink: string | null }> {
  const params: Record<string, string> = {};
  if (context?.source) params.source = context.source;
  if (context?.daysUntilDeparture != null && Number.isFinite(context.daysUntilDeparture)) {
    params.daysUntilDeparture = String(context.daysUntilDeparture);
  }
  if (context?.date && /^\d{4}-\d{2}-\d{2}$/.test(context.date)) params.date = context.date;
  Object.assign(params, omioPositionParams(await resolveOmioRoute(context?.route)));
  const res = await authApi.get(`/journeys/${journeyId}/legs/${legId}/affiliate/omio`, { params });
  return res.data;
}

/**
 * Persists the number of nights spent at a stop.
 *
 * "Nights" is stored on the stop as `stopDurationHours` (nights * 24); the stop
 * is the single source of truth for its duration. Updates only that field.
 *
 * @param stopId - ID of the journey stop to update
 * @param nights - Number of nights to spend at the stop (>= 0)
 */
export async function updateStopNights(stopId: string, nights: number): Promise<void> {
  await authApi.put(`/journey-stops/${stopId}`, { stopDurationHours: nights * 24 });
}

/**
 * Creates a new journey entry. Supports optionally specifying a source journey (for duplicating/branching)
 * and including initial input parameters for the journey creation flow.
 *
 * @template T - The return data type (defaults to any)
 * @param journey - The JourneyType object representing the new journey
 * @param sourceJourneyId - (optional) The ID of the source journey, if this is a new version
 * @param inputParameters - (optional) Initial input parameters for journey creation
 * @returns Promise resolving to EditResponse reflecting creation success or error
 */
export async function approveCreateJourney<T = any>(journey: JourneyType, sourceJourneyId?: string, inputParameters?: any): Promise<EditResponse<T>> {
  try {
    const payload: any = { journey };
    if (sourceJourneyId) payload.sourceJourneyId = sourceJourneyId;
    if (inputParameters) payload.inputParameters = inputParameters;
    const res = await authApi.post('/journeys', payload);
    return res.data as EditResponse<T>;
  } catch (error: any) {
    const code = error?.response?.data?.error?.code || 'CREATE_ERROR';
    const message = error?.response?.data?.error?.message || 'Unable to save journey. Please try again.';
    return { success: false, error: { code, message } };
  }
}

